/**
 * Connectivity modes (#252) — pass windows API (api function).
 * Modem mode/config itself goes through `edgeDeviceService.updateModemSettings`.
 */
import { apiRequest } from './api';
import { getAuthHeaders } from './edgeDeviceService';
import { CENTRAL_API_URL } from '@/config/env';

export interface PassWindowRow {
  id: string;
  deviceId: string;
  modemImei: string | null;
  start: string;
  end: string;
  satelliteId: string | null;
  source: 'upload' | 'api';
  createdAt: string | null;
}

export async function listPassWindows(deviceId: string, opts?: { from?: Date; to?: Date; limit?: number }): Promise<PassWindowRow[]> {
  const q = new URLSearchParams();
  if (opts?.from) q.set('from', opts.from.toISOString());
  if (opts?.to) q.set('to', opts.to.toISOString());
  if (opts?.limit) q.set('limit', String(opts.limit));
  const qs = q.toString();
  const res = await apiRequest<{ windows: PassWindowRow[] }>(`/devices/${encodeURIComponent(deviceId)}/pass-windows${qs ? `?${qs}` : ''}`);
  return res.windows || [];
}

export async function uploadPassWindowsCsv(deviceId: string, csv: string): Promise<{ created: number; skipped: number }> {
  return apiRequest(`/devices/${encodeURIComponent(deviceId)}/pass-windows`, {
    method: 'POST',
    body: JSON.stringify({ csv }),
  });
}

export async function deletePassWindow(deviceId: string, windowId: string): Promise<void> {
  await apiRequest(`/devices/${encodeURIComponent(deviceId)}/pass-windows/${encodeURIComponent(windowId)}`, { method: 'DELETE' });
}

// ── Reads (api function; user token or API key) ───────────────────────────

export interface RangeOpts { from?: Date; to?: Date; modemImei?: string; limit?: number; cursor?: string; order?: 'asc' | 'desc' }

function qs(opts: RangeOpts & Record<string, any>): string {
  const q = new URLSearchParams();
  if (opts.from) q.set('from', opts.from.toISOString());
  if (opts.to) q.set('to', opts.to.toISOString());
  if (opts.modemImei) q.set('modemImei', opts.modemImei);
  if (opts.limit) q.set('limit', String(opts.limit));
  if (opts.cursor) q.set('cursor', opts.cursor);
  for (const k of ['granularity', 'kinds', 'order']) if (opts[k]) q.set(k, String(opts[k]));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export interface ConnectivityEventRow {
  id: string; seq: number; ts: string; kind: string; mode?: string | null; windowId?: string | null;
  cause?: { code?: string | number; text?: string } | null; plmn?: string | null; rat?: string | null; band?: string | null;
  earfcn?: number | null; pci?: number | null; rsrp?: number | null; rsrpNormalized?: number | null;
  attachTimeMs?: number | null; backoffSec?: number | null; attempt?: number | null; detail?: any; modemImei: string;
}
export interface ScanResultRow {
  id: string; ts: string; durationMs: number | null; method: string | null; windowId?: string | null; targetSeen: boolean; modemImei: string;
  cells: Array<{ plmn: string | null; band: number | null; earfcn: number | null; pci: number | null; rsrp: number | null; rsrq: number | null; rsrpNormalized: number | null; target: boolean }>;
}
export interface RollupRow {
  id: string; modemImei: string; bucketStart: string; granularity: 'hour' | 'day';
  attempts?: number; attaches?: number; failures?: number; failuresByCause?: Record<string, number>; detaches?: number;
  attachedSec?: number; scans?: number; targetDetections?: number; probes?: number; probeFail?: number; rttHist?: Record<string, number>;
  derived: { uptimePct: number | null; attachSuccessRate: number | null; avgAttachMs: number | null; detectionRate: number | null; avgDetectRsrp: number | null; probeLossPct: number | null; avgRttMs: number | null; rttP50Ms: number | null; rttP95Ms: number | null; avgTtfbMs: number | null };
}
export interface ModemArtifactRow {
  id: string; type: string; filename: string; sizeBytes: number; contentType: string; uploadedAt: string; modemImei: string;
  trigger?: string; windowStart?: string | null; windowEnd?: string | null; skipReason?: string; meta?: any;
}

export const getConnectivityEvents = (deviceId: string, opts: RangeOpts & { kinds?: string } = {}) =>
  apiRequest<{ items: ConnectivityEventRow[]; nextCursor: string | null }>(`/devices/${encodeURIComponent(deviceId)}/connectivity/events${qs(opts)}`);
export const getScanResults = (deviceId: string, opts: RangeOpts = {}) =>
  apiRequest<{ items: ScanResultRow[]; nextCursor: string | null }>(`/devices/${encodeURIComponent(deviceId)}/connectivity/scans${qs(opts)}`);
export const getConnectivityRollups = (deviceId: string, opts: RangeOpts & { granularity?: 'hour' | 'day' } = {}) =>
  apiRequest<{ granularity: 'hour' | 'day'; items: RollupRow[] }>(`/devices/${encodeURIComponent(deviceId)}/connectivity/rollups${qs(opts)}`);
export const getProbeBatches = (deviceId: string, opts: RangeOpts = {}) =>
  apiRequest<{ items: any[]; nextCursor: string | null }>(`/devices/${encodeURIComponent(deviceId)}/connectivity/probes${qs(opts)}`);
export const getModemArtifacts = (deviceId: string, opts: RangeOpts = {}) =>
  apiRequest<{ items: ModemArtifactRow[] }>(`/devices/${encodeURIComponent(deviceId)}/connectivity/artifacts${qs(opts)}`);
export const getModemArtifactSignedUrl = (deviceId: string, modemImei: string, artifactId: string) =>
  apiRequest<{ artifactId: string; signedUrl: string; expiresAt: string; filename: string; sizeBytes: number; contentType: string }>(
    `/devices/${encodeURIComponent(deviceId)}/modems/${encodeURIComponent(modemImei)}/artifacts/${encodeURIComponent(artifactId)}/signed-url`);
export const getDevicePosition = (deviceId: string) =>
  apiRequest<{ positions: Array<{ modemImei: string; lat: number; lon: number; altM: number | null; hdop: number | null; fixAt: string | null }> }>(`/devices/${encodeURIComponent(deviceId)}/position`);

// ── Actions (central-server; needs the device WS) ─────────────────────────

async function centralPost(path: string, body: any): Promise<any> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${CENTRAL_API_URL}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || json.message || `Request failed (${res.status})`);
  return json;
}
export const grabCapture = (deviceId: string, modemImei: string, minutes: number, types?: string[]) =>
  centralPost(`/devices/${encodeURIComponent(deviceId)}/modems/${encodeURIComponent(modemImei)}/capture/grab`, { minutes, types });
export const huntScanNow = (deviceId: string, modemImei: string, attach = false) =>
  centralPost(`/devices/${encodeURIComponent(deviceId)}/modems/${encodeURIComponent(modemImei)}/hunt/scan-now`, { attach });
